import React from 'react'
import { MONTH_NAMES } from '../../../utils/projectAnalysisPeriod'

export interface ProjectDetailPeriodControlsProps {
  detailMode: 'fy' | 'calendar'
  onDetailModeChange: (m: 'fy' | 'calendar') => void
  detailFY: string
  onDetailFYChange: (v: string) => void
  detailQ: string
  onDetailQChange: (v: string) => void
  detailYear: string
  onDetailYearChange: (v: string) => void
  detailMonth: string
  onDetailMonthChange: (v: string) => void
  detailFYOptions: { value: string; label: string }[]
  detailYearOptions: number[]
  /** e.g. `w-full lg:w-auto` when sitting in the detail header bar */
  className?: string
}

/** Same toolbar tokens as `ProjectOverviewFilters` (header filters). */
const sel =
  'text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500'

export function ProjectDetailPeriodControls({
  detailMode,
  onDetailModeChange,
  detailFY,
  onDetailFYChange,
  detailQ,
  onDetailQChange,
  detailYear,
  onDetailYearChange,
  detailMonth,
  onDetailMonthChange,
  detailFYOptions,
  detailYearOptions,
  className,
}: ProjectDetailPeriodControlsProps) {
  return (
    <div
      className={[
        'flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 min-w-0',
        className ?? 'w-full',
      ].join(' ')}
    >
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Period
        </span>
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {(
            [
              ['fy', 'Indian FY'],
              ['calendar', 'Calendar'],
            ] as const
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => onDetailModeChange(mode)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                detailMode === mode
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {detailMode === 'fy' ? (
        <>
          <div className="h-4 w-px bg-slate-200 shrink-0" />
          <select
            value={detailFY}
            onChange={(e) => onDetailFYChange(e.target.value)}
            className={`${sel} min-w-[7rem]`}
          >
            {detailFYOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={detailQ}
            onChange={(e) => onDetailQChange(e.target.value)}
            className={`${sel} min-w-[4.5rem]`}
          >
            <option value="all">All Q</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </select>
        </>
      ) : (
        <>
          <div className="h-4 w-px bg-slate-200 shrink-0" />
          <select
            value={detailYear}
            onChange={(e) => onDetailYearChange(e.target.value)}
            className={`${sel} min-w-[5.5rem]`}
          >
            <option value="all">All years</option>
            {detailYearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
          <select
            value={detailMonth}
            onChange={(e) => onDetailMonthChange(e.target.value)}
            className={`${sel} min-w-[7rem]`}
          >
            <option value="all">All months</option>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={String(i + 1)}>
                {name}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  )
}
